import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { apartmentsApi } from '../../../api/apartmentsApi';
import {
  paymentApi,
  ReceivableCustomerLedgerLine,
  ReceivableCustomerLedgerResponse,
} from '../../../api/paymentApi';
import BillingPeriodMonthPicker from '../../../components/common/BillingPeriodMonthPicker';
import { billingPeriodToDateRange } from '../../../utils/billingPeriodPicker';
import { FeeCategoryOption, FeeCategoryPillSelect } from './FeeCategoryPillSelect';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const formatLedgerMoney = (value: number) => {
  const rounded = Math.round(value);
  if (rounded === 0) return '—';
  return `${new Intl.NumberFormat('vi-VN').format(rounded)}đ`;
};

const formatDisplayDate = (value: string | null) => {
  if (!value) return '—';
  const [y, m, d] = value.split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
};

const isTransactionLine = (line: ReceivableCustomerLedgerLine) =>
  line.kind === 'entry' || line.kind === 'incurred' || line.kind === 'payment';

interface ReceivableCustomerLedgerDetailProps {
  buildingId: string;
  apartmentId: string;
  apartmentCode: string;
  ownerName: string;
  collectionPeriod: string;
  onCollectionPeriodChange: (value: string) => void;
  feeCategoryId: string;
  onFeeCategoryChange: (value: string) => void;
  feeCategories: FeeCategoryOption[];
  onBack: () => void;
}

export const ReceivableCustomerLedgerDetail: React.FC<ReceivableCustomerLedgerDetailProps> = ({
  buildingId,
  apartmentId: initialApartmentId,
  apartmentCode: initialApartmentCode,
  ownerName: initialOwnerName,
  collectionPeriod,
  onCollectionPeriodChange,
  feeCategoryId,
  onFeeCategoryChange,
  feeCategories,
  onBack,
}) => {
  const [draftApartmentCode, setDraftApartmentCode] = useState(initialApartmentCode);
  const [committedApartment, setCommittedApartment] = useState({
    id: initialApartmentId,
    code: initialApartmentCode,
    ownerName: initialOwnerName,
  });
  const [data, setData] = useState<ReceivableCustomerLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolvingApartment, setResolvingApartment] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [jumpPage, setJumpPage] = useState('1');

  useEffect(() => {
    setCommittedApartment({
      id: initialApartmentId,
      code: initialApartmentCode,
      ownerName: initialOwnerName,
    });
    setDraftApartmentCode(initialApartmentCode);
  }, [initialApartmentId]);

  const commitApartmentSearch = useCallback(async () => {
    const term = draftApartmentCode.trim();
    if (!term || !buildingId) return;
    if (
      term.toLowerCase() === committedApartment.code.toLowerCase() &&
      committedApartment.id
    ) {
      return;
    }

    setResolvingApartment(true);
    try {
      const result = await apartmentsApi.getApartmentsPage({
        buildingId,
        search: term,
        page: 1,
        limit: 30,
      });
      const exact = result.data.find(
        (apartment) => apartment.code.toLowerCase() === term.toLowerCase(),
      );
      const match = exact ?? result.data[0];
      if (!match) {
        toast.error('Không tìm thấy căn hộ');
        return;
      }
      setCommittedApartment({
        id: match.id,
        code: match.code,
        ownerName: match.ownerName || '',
      });
      setDraftApartmentCode(match.code);
    } catch {
      toast.error('Không thể tìm căn hộ');
    } finally {
      setResolvingApartment(false);
    }
  }, [buildingId, committedApartment.code, committedApartment.id, draftApartmentCode]);

  const { dateFrom, dateTo } = useMemo(
    () => billingPeriodToDateRange(collectionPeriod),
    [collectionPeriod],
  );

  const loadLedger = useCallback(async () => {
    if (!buildingId || !committedApartment.id) return;

    setLoading(true);
    try {
      const response = await paymentApi.getReceivableCustomerLedger({
        buildingId,
        apartmentId: committedApartment.id,
        apartmentCode: committedApartment.code,
        periodFrom: collectionPeriod,
        periodTo: collectionPeriod,
        feeCategoryId: feeCategoryId || undefined,
        dateFrom,
        dateTo,
      });
      setData(response);
      setCommittedApartment((prev) => ({
        id: response.apartmentId,
        code: response.apartmentCode,
        ownerName: response.ownerName,
      }));
      setDraftApartmentCode(response.apartmentCode);
    } catch {
      toast.error('Không thể tải chi tiết công nợ');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    buildingId,
    committedApartment.code,
    committedApartment.id,
    collectionPeriod,
    dateFrom,
    dateTo,
    feeCategoryId,
  ]);

  useEffect(() => {
    setPage(1);
    setJumpPage('1');
  }, [committedApartment.id, feeCategoryId, collectionPeriod, pageSize]);

  useEffect(() => {
    const timer = setTimeout(loadLedger, 250);
    return () => clearTimeout(timer);
  }, [loadLedger]);

  const transactionLines = useMemo(
    () => (data?.lines || []).filter(isTransactionLine),
    [data?.lines],
  );
  const lineCount = transactionLines.length;
  const pageStart = lineCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = lineCount === 0 ? 0 : Math.min(page * pageSize, lineCount);

  const totalPages = Math.max(1, Math.ceil(transactionLines.length / pageSize));
  const pagedLines = transactionLines.slice((page - 1) * pageSize, page * pageSize);

  const openingLine = data?.lines.find((line) => line.kind === 'opening');
  const totalLine = data?.lines.find((line) => line.kind === 'total');
  const closingLine = data?.lines.find((line) => line.kind === 'closing');

  const goToPage = (next: number) => {
    const clamped = Math.min(totalPages, Math.max(1, next));
    setPage(clamped);
    setJumpPage(String(clamped));
  };

  const renderAmountCells = (line: ReceivableCustomerLedgerLine) => {
    if (line.kind === 'opening' || line.kind === 'closing') {
      return (
        <>
          <td
            className={`px-3 py-2.5 text-right text-sm border-b border-gray-100 ${
              line.kind === 'closing' ? 'font-semibold' : ''
            }`}
          >
            {formatLedgerMoney(line.debit)}
          </td>
          <td
            className={`px-3 py-2.5 text-right text-sm border-b border-gray-100 ${
              line.kind === 'closing' ? 'font-semibold' : ''
            }`}
          >
            {formatLedgerMoney(line.credit)}
          </td>
        </>
      );
    }
    if (line.kind === 'total') {
      return (
        <>
          <td className="px-3 py-2.5 text-right text-sm border-b border-gray-100 font-semibold bg-gray-50">
            {formatLedgerMoney(line.debit)}
          </td>
          <td className="px-3 py-2.5 text-right text-sm border-b border-gray-100 font-semibold bg-gray-50">
            {formatLedgerMoney(line.credit)}
          </td>
        </>
      );
    }
    return (
      <>
        <td className="px-3 py-2.5 text-right text-sm border-b border-gray-100">
          {formatLedgerMoney(line.debit)}
        </td>
        <td className="px-3 py-2.5 text-right text-sm border-b border-gray-100">
          {formatLedgerMoney(line.credit)}
        </td>
      </>
    );
  };

  const renderRow = (line: ReceivableCustomerLedgerLine) => (
    <tr
      key={line.id}
      className={
        line.kind === 'total'
          ? 'bg-gray-50 font-semibold'
          : line.kind === 'opening' || line.kind === 'closing'
            ? 'bg-sky-50/60'
            : 'hover:bg-gray-50/80'
      }
    >
      <td className="px-3 py-2.5 text-sm border-b border-gray-100 whitespace-nowrap">
        {formatDisplayDate(line.date)}
      </td>
      <td className="px-3 py-2.5 text-sm border-b border-gray-100 max-w-[320px]">
        {line.description || '—'}
      </td>
      <td className="px-3 py-2.5 text-sm border-b border-gray-100 whitespace-nowrap">
        {line.typeCode || '—'}
      </td>
      {renderAmountCells(line)}
      <td className="px-3 py-2.5 text-center border-b border-gray-100">
        {line.kind === 'entry' && line.serviceFeeInvoiceId && line.debit > 0 ? (
          <Link
            to={`/apartments/${committedApartment.id}/details`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:text-cyan-700 hover:border-cyan-300"
            title="Xem căn hộ / phí dịch vụ"
          >
            <EyeIcon className="w-4 h-4" />
          </Link>
        ) : null}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Chi tiết công nợ phải thu theo khách hàng
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {committedApartment.code} · {committedApartment.ownerName}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="self-start sm:self-auto rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          ← Quay lại
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-3">
          <FeeCategoryPillSelect
            label="Loại phí"
            value={feeCategoryId}
            options={feeCategories}
            onChange={onFeeCategoryChange}
          />
          <input
            type="text"
            value={draftApartmentCode}
            onChange={(e) => setDraftApartmentCode(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                await commitApartmentSearch();
              }
            }}
            onBlur={() => {
              const term = draftApartmentCode.trim();
              if (
                term &&
                term.toLowerCase() !== committedApartment.code.toLowerCase()
              ) {
                commitApartmentSearch();
              }
            }}
            placeholder="Căn hộ"
            disabled={resolvingApartment}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[140px] disabled:bg-gray-50"
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">Kỳ báo cáo</span>
            <BillingPeriodMonthPicker
              value={collectionPeriod}
              onChange={onCollectionPeriodChange}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-xs font-semibold uppercase text-gray-600 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 text-left">Ngày</th>
                <th className="px-3 py-3 text-left">Diễn giải</th>
                <th className="px-3 py-3 text-left">Mã loại</th>
                <th colSpan={2} className="px-3 py-2 text-center border-l border-gray-200">
                  Phát sinh
                </th>
                <th className="px-3 py-3 text-center border-l border-gray-200">Thao tác</th>
              </tr>
              <tr className="text-xs font-semibold uppercase text-gray-600 bg-gray-50 border-b border-gray-200">
                <th colSpan={3} />
                <th className="px-3 py-2 text-right border-l border-gray-200">Nợ</th>
                <th className="px-3 py-2 text-right">Có</th>
                <th className="border-l border-gray-200" />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Đang tải...
                  </td>
                </tr>
              ) : !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Chưa có dữ liệu
                  </td>
                </tr>
              ) : (
                <>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="px-2 py-1 text-center text-xs text-cyan-700 bg-cyan-50">
                        Đang cập nhật...
                      </td>
                    </tr>
                  )}
                  {openingLine && renderRow(openingLine)}
                  {pagedLines.map((line) => renderRow(line))}
                  {totalLine && renderRow(totalLine)}
                  {closingLine && renderRow(closingLine)}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600 space-y-2">
          <div>
            {lineCount > 0 ? (
              <>
                Đang xem{' '}
                <span className="font-medium text-gray-800 tabular-nums">
                  {pageStart}–{pageEnd}
                </span>{' '}
                trong{' '}
                <span className="font-medium text-gray-800 tabular-nums">{lineCount}</span> dòng
                <span className="text-gray-400 mx-2">·</span>
                <span className="font-medium text-gray-800 tabular-nums">
                  Trang {page} / {totalPages}
                </span>
              </>
            ) : (
              'Tổng 0 dòng'
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
                setJumpPage('1');
              }}
              className="rounded border border-gray-200 bg-white px-2 py-1 text-sm"
              aria-label="Số dòng mỗi trang"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}/trang</option>
              ))}
            </select>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                  aria-label="Trang trước"
                >
                  ‹
                </button>
                <span className="min-w-[5.5rem] text-center font-semibold text-gray-800 tabular-nums">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                  aria-label="Trang sau"
                >
                  ›
                </button>
              </div>
              <span className="flex items-center gap-1.5 text-gray-600">
                Nhảy tới
                <input
                  type="text"
                  inputMode="numeric"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const parsed = Number(jumpPage);
                      if (parsed) goToPage(parsed);
                    }
                  }}
                  className="w-12 rounded border border-gray-200 px-2 py-1 text-center text-sm tabular-nums"
                  aria-label="Số trang"
                />
                <span className="text-gray-500 tabular-nums">/ {totalPages}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivableCustomerLedgerDetail;
